import clsx from 'clsx'
import type { ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'
import type { Priority } from './types'
import i18n from '@/i18n'

/** Active i18n locale for date / number formatting. Falls back to 'en'.
 *  Read at call time so date helpers update immediately when the user
 *  switches language without needing to re-render their callers. */
function activeLocale(): string {
  // Lazy import to avoid pulling i18n into circular contexts at startup.
  // The module is initialised in main.tsx before any UI mounts, so by
  // the time this is called, `i18n` is ready and has its `language`
  // resolved (either the saved pref or the browser-detected one).
  const lang = (i18n.language ?? 'en').split('-')[0]
  return lang
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatRelative(iso: string): string {
  const diffSec = Math.round((Date.now() - new Date(iso).getTime()) / 1000)
  if (diffSec < 60) return i18n.t('date.relative.secondsAgo', { count: diffSec })
  if (diffSec < 3600) return i18n.t('date.relative.minutesAgo', { count: Math.floor(diffSec / 60) })
  if (diffSec < 86400) return i18n.t('date.relative.hoursAgo', { count: Math.floor(diffSec / 3600) })
  return i18n.t('date.relative.daysAgoShort', { count: Math.floor(diffSec / 86400) })
}

/** Short absolute date, e.g. "May 14" / "14 mai" depending on locale,
 *  with the year appended only when the date isn't in the current year. */
export function formatDate(iso: string): string {
  const d = new Date(iso)
  const sameYear = d.getFullYear() === new Date().getFullYear()
  return d.toLocaleDateString(activeLocale(), {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  })
}

/** Full localized date + time, e.g. "14 mai 2026 à 14:32" / "May 14, 2026
 *  at 2:32 PM". Used for the precise-timestamp tooltips behind the relative
 *  « il y a 2j » labels. */
export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(activeLocale(), {
    dateStyle: 'long',
    timeStyle: 'short',
  })
}

/** Human-readable label for an AI-extracted date (`YYYY-MM-DD`).
 *  Prefixes the day-of-week label with a friendly relative marker
 *  ("Today", "Tomorrow", "Yesterday") when applicable, falling back to
 *  the long localized weekday + date. Used for the card's calendar-icon
 *  tooltip so the user immediately reads "Tomorrow — Saturday May 17"
 *  rather than having to mentally resolve a bare ISO date. */
export function formatAiDate(iso: string): string {
  // Parse YYYY-MM-DD as a LOCAL date (not UTC) so "today" comparison
  // works on the user's wall clock. `new Date('2026-05-17')` would
  // parse as UTC midnight, then drift one day in negative-UTC zones.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim())
  if (!m) return iso
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.round((d.getTime() - today.getTime()) / 86_400_000)

  const long = d.toLocaleDateString(activeLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })

  if (diffDays === 0) return `${i18n.t('date.today')} — ${long}`
  if (diffDays === 1) return `${i18n.t('date.tomorrow')} — ${long}`
  if (diffDays === -1) return `${i18n.t('date.yesterday')} — ${long}`
  if (diffDays > 1 && diffDays <= 7)
    return `${i18n.t(diffDays === 1 ? 'date.inDaysOne' : 'date.inDaysOther', { count: diffDays })} — ${long}`
  if (diffDays < -1 && diffDays >= -7) {
    const abs = -diffDays
    return `${i18n.t(abs === 1 ? 'date.daysAgoOne' : 'date.daysAgoOther', { count: abs })} — ${long}`
  }
  return long.charAt(0).toUpperCase() + long.slice(1)
}

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/** Short human label for a user-set schedule (all-day or timed, single date
 *  or period). Uses the active locale. */
export function formatScheduled(
  start: string,
  end: string | null,
  allDay: boolean,
): string {
  const s = new Date(start)
  if (isNaN(s.getTime())) return start
  const e = end ? new Date(end) : null
  const dateOpts: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
  const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' }
  const loc = activeLocale()
  const sDate = s.toLocaleDateString(loc, dateOpts)
  if (allDay) {
    if (e) {
      const eDate = e.toLocaleDateString(loc, dateOpts)
      return sDate === eDate ? sDate : `${sDate} → ${eDate}`
    }
    return sDate
  }
  const sTime = s.toLocaleTimeString(loc, timeOpts)
  if (e) {
    const eDate = e.toLocaleDateString(loc, dateOpts)
    const eTime = e.toLocaleTimeString(loc, timeOpts)
    if (sDate === eDate) return `${sDate} ${sTime} → ${eTime}`
    return `${sDate} ${sTime} → ${eDate} ${eTime}`
  }
  return `${sDate} ${sTime}`
}

/** Build a Google Calendar "create event" deep link (no OAuth — opens the
 *  prefilled event editor in a new tab). Returns null if no valid start. */
export function googleCalendarUrl(opts: {
  title: string
  details?: string | null
  start: string
  end?: string | null
  allDay: boolean
}): string | null {
  const start = new Date(opts.start)
  if (isNaN(start.getTime())) return null

  const allDayStamp = (d: Date) =>
    `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}`
  const timedStamp = (d: Date) =>
    `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}T` +
    `${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}Z`

  let dates: string
  if (opts.allDay) {
    // Google treats the all-day end as EXCLUSIVE → add one day.
    const endBase = opts.end ? new Date(opts.end) : new Date(start)
    const endExcl = new Date(endBase)
    endExcl.setDate(endExcl.getDate() + 1)
    dates = `${allDayStamp(start)}/${allDayStamp(endExcl)}`
  } else {
    const end = opts.end ? new Date(opts.end) : new Date(start.getTime() + 3_600_000)
    dates = `${timedStamp(start)}/${timedStamp(end)}`
  }

  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: opts.title.trim() || '—',
    dates,
  })
  if (opts.details?.trim()) params.set('details', opts.details.trim())
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}

/** Best display name for a user: explicit display_name, else "First Last"
 *  (the collaborator may have filled first/last but not the display_name
 *  field). Returns '' when nothing is set — callers add their own fallback. */
export function profileName(
  p?: {
    display_name?: string | null
    first_name?: string | null
    last_name?: string | null
  } | null,
): string {
  const dn = p?.display_name?.trim()
  if (dn) return dn
  return `${p?.first_name ?? ''} ${p?.last_name ?? ''}`.replace(/\s+/g, ' ').trim()
}

// ── Google-Calendar-style recurrence engine (shared shape with the
//    send-reminders edge function, which mirrors this logic in Deno) ──
export type ReminderFrequency =
  | 'hourly'
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'yearly'

export interface ReminderRule {
  freq: ReminderFrequency
  interval: number
  /** Weekly: weekdays to fire on (0=Sun … 6=Sat). Empty/undefined = anchor's. */
  byweekday?: number[] | null
  /** Monthly: 'day' (same day-of-month) or 'nth' (same nth weekday). */
  monthlyMode?: 'day' | 'nth' | null
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate()
}

/** Which occurrence (1-based) of its own weekday `d` is within its month. */
function nthWeekdayOfMonth(d: Date): number {
  return Math.floor((d.getDate() - 1) / 7) + 1
}

/** Date of the `nth` (1..5) `dow` (0=Sun) of month, at h:m. null if absent. */
function nthWeekdayDate(
  year: number,
  monthIndex: number,
  dow: number,
  nth: number,
  h: number,
  min: number,
): Date | null {
  const first = new Date(year, monthIndex, 1)
  const offset = (dow - first.getDay() + 7) % 7
  const day = 1 + offset + (nth - 1) * 7
  if (day > daysInMonth(year, monthIndex)) return null
  return new Date(year, monthIndex, day, h, min, 0, 0)
}

const GUARD = 100_000

/** First occurrence strictly after `afterMs`, enumerating from the DTSTART
 *  `anchorIso` per the rule. Returns null if the anchor is invalid or no
 *  occurrence is found within the guard. The anchor itself qualifies when it
 *  is already after `afterMs`. */
export function nextReminderAfter(
  anchorIso: string,
  rule: ReminderRule,
  afterMs: number,
): string | null {
  const anchor = new Date(anchorIso)
  if (isNaN(anchor.getTime())) return null
  const interval = Math.max(1, rule.interval || 1)
  const aMs = anchor.getTime()
  const h = anchor.getHours()
  const min = anchor.getMinutes()

  if (rule.freq === 'hourly') {
    const out = new Date(anchor)
    let g = 0
    while (out.getTime() <= afterMs && g < GUARD) {
      out.setHours(out.getHours() + interval)
      g++
    }
    return out.toISOString()
  }

  if (rule.freq === 'daily') {
    const out = new Date(anchor)
    let g = 0
    while (out.getTime() <= afterMs && g < GUARD) {
      out.setDate(out.getDate() + interval)
      g++
    }
    return out.toISOString()
  }

  if (rule.freq === 'weekly') {
    const days = (
      rule.byweekday && rule.byweekday.length ? rule.byweekday : [anchor.getDay()]
    )
      .slice()
      .sort((a, b) => a - b)
    // Reference: the Sunday that starts the anchor's week, at the fire time.
    const weekStart = new Date(anchor)
    weekStart.setDate(weekStart.getDate() - weekStart.getDay())
    weekStart.setHours(h, min, 0, 0)
    let w = 0
    let g = 0
    while (g < GUARD) {
      if (w % interval === 0) {
        for (const dow of days) {
          const cand = new Date(weekStart)
          cand.setDate(cand.getDate() + w * 7 + dow)
          const ms = cand.getTime()
          if (ms > afterMs && ms >= aMs) return cand.toISOString()
        }
      }
      w++
      g++
    }
    return null
  }

  if (rule.freq === 'monthly') {
    const mode = rule.monthlyMode ?? 'day'
    const baseDay = anchor.getDate()
    const dow = anchor.getDay()
    const nth = nthWeekdayOfMonth(anchor)
    let m = 0
    let g = 0
    while (g < GUARD) {
      const year = anchor.getFullYear()
      const monthIndex = anchor.getMonth() + m * interval
      let cand: Date | null
      if (mode === 'nth') {
        const y = year + Math.floor(monthIndex / 12)
        const mi = ((monthIndex % 12) + 12) % 12
        cand = nthWeekdayDate(y, mi, dow, nth, h, min)
      } else {
        const y = year + Math.floor(monthIndex / 12)
        const mi = ((monthIndex % 12) + 12) % 12
        cand = new Date(y, mi, Math.min(baseDay, daysInMonth(y, mi)), h, min, 0, 0)
      }
      if (cand) {
        const ms = cand.getTime()
        if (ms > afterMs && ms >= aMs) return cand.toISOString()
      }
      m++
      g++
    }
    return null
  }

  // yearly
  let y = 0
  let g = 0
  while (g < GUARD) {
    const cand = new Date(
      anchor.getFullYear() + y * interval,
      anchor.getMonth(),
      anchor.getDate(),
      h,
      min,
      0,
      0,
    )
    const ms = cand.getTime()
    if (ms > afterMs && ms >= aMs) return cand.toISOString()
    y++
    g++
  }
  return null
}

export type MediaKind = 'image' | 'video' | 'audio' | 'pdf' | 'file'

/** Classify a media URL by its file extension. Used by cards + detail modal. */
export function mediaKind(url: string): MediaKind {
  if (/\.(jpe?g|png|gif|webp|avif)(\?|$)/i.test(url)) return 'image'
  if (/\.(mp4|webm|mov|3gp|m4v)(\?|$)/i.test(url)) return 'video'
  if (/\.(ogg|oga|opus|mp3|m4a|aac|wav|amr)(\?|$)/i.test(url)) return 'audio'
  if (/\.pdf(\?|$)/i.test(url)) return 'pdf'
  return 'file'
}

/** A Word .docx (the only Word format mammoth can render in-browser; the
 *  legacy binary .doc is not supported and stays a plain download). */
export function isDocx(url: string): boolean {
  return /\.docx(\?|$)/i.test(url)
}

/** Initials from a name or email, for avatar fallbacks. */
export function initialsOf(nameOrEmail: string): string {
  const base = (nameOrEmail || '?').split('@')[0].replace(/[^\p{L}\p{N}\s.]/gu, ' ').trim()
  const parts = base.split(/[\s.]+/).filter(Boolean).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase()).join('') || '?'
}

/** Stable pastel avatar color from an id. */
const AVATAR_PALETTE = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-fuchsia-100 text-fuchsia-700',
  'bg-teal-100 text-teal-700',
  'bg-orange-100 text-orange-700',
]
export function avatarColor(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length]
}

/** Stable label chip color from the label text. */
const LABEL_PALETTE = [
  'bg-rose-100 text-rose-700 ring-rose-200',
  'bg-amber-100 text-amber-800 ring-amber-200',
  'bg-emerald-100 text-emerald-700 ring-emerald-200',
  'bg-sky-100 text-sky-700 ring-sky-200',
  'bg-violet-100 text-violet-700 ring-violet-200',
  'bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-200',
  'bg-teal-100 text-teal-700 ring-teal-200',
  'bg-orange-100 text-orange-700 ring-orange-200',
  'bg-indigo-100 text-indigo-700 ring-indigo-200',
  'bg-lime-100 text-lime-800 ring-lime-200',
]
export function labelColor(label: string): string {
  const key = label.trim().toLowerCase()
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return LABEL_PALETTE[h % LABEL_PALETTE.length]
}

/** Localized priority label. Reads via i18next directly so it works
 *  outside React (pure utility functions, sort comparators, etc.).
 *  Use this instead of `PRIORITY_META[p].label` which is now empty. */
export function priorityLabel(p: Priority): string {
  switch (p) {
    case 'urgent': return i18n.t('cardMenu.priorityUrgent')
    case 'high': return i18n.t('cardMenu.priorityHigh')
    case 'medium': return i18n.t('cardMenu.priorityMedium')
    case 'low': return i18n.t('cardMenu.priorityLow')
  }
}

/** Display metadata for each priority level (ordered most → least urgent).
 *  `label` left empty for backward-compat — call `priorityLabel(p)` for
 *  the localized string. The rest (chip / dot / shadow / rank) is style
 *  metadata that doesn't need translation. */
export const PRIORITY_META: Record<
  Priority,
  {
    label: string
    chip: string
    dot: string
    rank: number
    /** Card box-shadow tinted with the priority's accent colour, applied
     *  on the TicketCard root in lieu of the neutral `--shadow-card`. The
     *  inner sharp shadow keeps the elevation cue; the outer soft tint
     *  hints at the priority at a glance, without dominating. */
    shadow: string
  }
> = {
  urgent: {
    label: 'Urgent',
    chip: 'bg-[var(--color-danger-bg)] text-[var(--color-danger)] ring-red-200',
    dot: 'bg-[var(--color-danger)]',
    rank: 0,
    shadow:
      'shadow-[0_2px_10px_-2px_rgb(239_68_68_/_0.30),_0_1px_3px_-1px_rgb(0_0_0_/_0.08)]',
  },
  high: {
    label: 'Haute',
    chip: 'bg-orange-100 text-orange-700 ring-orange-200',
    dot: 'bg-orange-500',
    rank: 1,
    shadow:
      'shadow-[0_2px_10px_-2px_rgb(249_115_22_/_0.28),_0_1px_3px_-1px_rgb(0_0_0_/_0.08)]',
  },
  medium: {
    label: 'Moyenne',
    chip: 'bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] ring-blue-200',
    dot: 'bg-[var(--color-accent)]',
    rank: 2,
    shadow:
      'shadow-[0_2px_10px_-2px_rgb(59_130_246_/_0.22),_0_1px_3px_-1px_rgb(0_0_0_/_0.08)]',
  },
  low: {
    label: 'Basse',
    chip: 'bg-slate-100 text-slate-600 ring-slate-200',
    dot: 'bg-slate-400',
    rank: 3,
    shadow:
      'shadow-[0_2px_10px_-2px_rgb(100_116_139_/_0.20),_0_1px_3px_-1px_rgb(0_0_0_/_0.08)]',
  },
}

export const PRIORITY_ORDER: Priority[] = ['urgent', 'high', 'medium', 'low']

/** A card counts as "urgent" — for the flame icon, the top-bar Urgent
 *  filter and its count — when EITHER the AI flagged it (ai_is_urgent)
 *  OR its priority was set to 'urgent' manually. The two are equivalent:
 *  a human marking a card urgent matters as much as the AI detecting it. */
export function isTicketUrgent(t: {
  ai_is_urgent?: boolean | null
  priority?: Priority | null
}): boolean {
  return !!t.ai_is_urgent || t.priority === 'urgent'
}

/** Dot colors a user can pick for a list/column or a project. 12 entries
 *  arranged as a soft pastel rainbow (cool half / warm half) — pickers
 *  render them as a 6-column grid so the whole palette fits in two rows
 *  without wrap surprises.
 *
 *   `dot`    — saturated 500-stop, used for the small list dots and the
 *              project header swatch (high-contrast status indicator).
 *   `bubble` — pastel 100-stop + matching 700-text, used for the larger
 *              project tiles in the sidebar AND for the project color
 *              picker swatches (so the picker previews exactly what the
 *              tile will look like). Includes the text-* class so an
 *              icon placed inside (the "selected" check) inherits the
 *              dark family colour for free.
 *
 *  Existing values (`slate`, `blue`, `teal`, `green`, `amber`, `orange`,
 *  `red`) are preserved as-is so legacy project / list rows keep the
 *  same colour after the palette extension. The 5 new keys (`pink`,
 *  `fuchsia`, `violet`, `indigo`, `cyan`) extend the spectrum. */
export const LIST_COLORS: {
  key: string
  label: string
  dot: string
  bubble: string
}[] = [
  // Top row — neutral + warm spectrum.
  { key: 'slate',   label: 'Ardoise',   dot: 'bg-slate-400',   bubble: 'bg-slate-100 text-slate-700' },
  { key: 'red',     label: 'Rouge',     dot: 'bg-rose-500',    bubble: 'bg-rose-100 text-rose-700' },
  { key: 'pink',    label: 'Rose',      dot: 'bg-pink-500',    bubble: 'bg-pink-100 text-pink-700' },
  { key: 'fuchsia', label: 'Fuchsia',   dot: 'bg-fuchsia-500', bubble: 'bg-fuchsia-100 text-fuchsia-700' },
  { key: 'orange',  label: 'Orange',    dot: 'bg-orange-500',  bubble: 'bg-orange-100 text-orange-700' },
  { key: 'amber',   label: 'Ambre',     dot: 'bg-amber-500',   bubble: 'bg-amber-100 text-amber-700' },
  // Bottom row — cool spectrum.
  { key: 'green',   label: 'Vert',      dot: 'bg-emerald-500', bubble: 'bg-emerald-100 text-emerald-700' },
  { key: 'teal',    label: 'Turquoise', dot: 'bg-teal-500',    bubble: 'bg-teal-100 text-teal-700' },
  { key: 'cyan',    label: 'Cyan',      dot: 'bg-cyan-500',    bubble: 'bg-cyan-100 text-cyan-700' },
  { key: 'blue',    label: 'Bleu',      dot: 'bg-blue-500',    bubble: 'bg-blue-100 text-blue-700' },
  { key: 'indigo',  label: 'Indigo',    dot: 'bg-indigo-500',  bubble: 'bg-indigo-100 text-indigo-700' },
  { key: 'violet',  label: 'Violet',    dot: 'bg-violet-500',  bubble: 'bg-violet-100 text-violet-700' },
]

/** Resolve a project's small-dot color (top bar swatch + move-dialog row).
 *  Uses the pastel `bubble` background (not the saturated `dot`) so the
 *  swatch matches what the colour picker preview shows AND what the
 *  sidebar tile renders. The `bubble` field is `"bg-X-100 text-X-700"`;
 *  we keep just the bg class since the dot has no inner text/icon.
 *  Falls back to a neutral surface when the project has no color set. */
// Deterministic fallback hue when a project has no explicit colour. MUST
// stay in sync with ProjectSidebar's AVATAR_PALETTE order (same hash, same
// hue sequence) so the top-bar dot matches the sidebar tile. Tailwind needs
// literal class names, hence the explicit map.
const ACCENT_FALLBACK_DOT = [
  'bg-rose-400',
  'bg-amber-400',
  'bg-emerald-400',
  'bg-sky-400',
  'bg-violet-400',
  'bg-fuchsia-400',
  'bg-teal-400',
  'bg-orange-400',
]
function fallbackDotForId(id: string): string {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return ACCENT_FALLBACK_DOT[h % ACCENT_FALLBACK_DOT.length]
}

export function projectDotClass(
  color: string | null | undefined,
  id?: string,
): string {
  if (color) {
    const found = LIST_COLORS.find((c) => c.key === color)
    if (found) return found.bubble.split(' ')[0]
  }
  // No explicit colour → mirror the sidebar's hashed tile hue (so the
  // top-bar swatch and the sidebar bubble agree), or neutral if no id.
  return id ? fallbackDotForId(id) : 'bg-[var(--color-surface-3)]'
}
/** Resolve a project's pastel bubble classes (sidebar tile). When unset,
 *  returns the caller-provided fallback (typically the deterministic
 *  hash-based palette so legacy projects still look distinct). */
export function projectBubbleClass(
  color: string | null | undefined,
  fallback: string,
): string {
  if (!color) return fallback
  return LIST_COLORS.find((c) => c.key === color)?.bubble ?? fallback
}

/** Translate the three trigger-created default list names (Reçu /
 *  En cours / Terminé) into the current UI locale. We match on BOTH the
 *  exact French string AND the intake/done flag so a custom user-created
 *  list that happens to share one of those names doesn't get rewritten.
 *  Once the user renames the list, the new name no longer matches and
 *  is rendered as-is. */
export function displayListName(list: {
  name: string
  is_intake?: boolean | null
  is_done?: boolean | null
}): string {
  if (list.is_intake && list.name === 'Reçu') return i18n.t('list.defaultIntake')
  if (list.is_done && list.name === 'Terminé') return i18n.t('list.defaultDone')
  if (!list.is_intake && !list.is_done && list.name === 'En cours')
    return i18n.t('list.defaultInProgress')
  return list.name
}

/** Resolve a list's dot color class — an explicit pick wins, else the
 *  semantic default (intake / done / in-progress). */
export function listDotClass(list: {
  color?: string | null
  is_intake?: boolean
  is_done?: boolean
}): string {
  if (list.color) {
    const found = LIST_COLORS.find((c) => c.key === list.color)
    if (found) return found.dot
  }
  if (list.is_intake) return 'bg-[var(--color-todo)]'
  if (list.is_done) return 'bg-[var(--color-done)]'
  return 'bg-[var(--color-progress)]'
}

/** Group a list of media URLs by kind. */
export function groupMedia(urls: string[]): Record<MediaKind, string[]> {
  const groups: Record<MediaKind, string[]> = {
    image: [],
    video: [],
    audio: [],
    pdf: [],
    file: [],
  }
  for (const u of urls) groups[mediaKind(u)].push(u)
  return groups
}
